import React, { FC, PropsWithChildren, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import cns from "classnames";

import ROUTES from "constants/routes";
import { USER_TYPE } from "constants/enums";
import { IUser } from "types";
import { ReactComponent as HamburgerIcon } from "icons/hamburger.svg";
import { ReactComponent as CloseIcon } from "icons/close.svg";
import { ReactComponent as LogoIcon } from "icons/logo_summit.svg";
import { ReactComponent as ExitIcon } from "icons/exit.svg";
import { ReactComponent as PhoneIcon } from "icons/phone.svg";
import { ReactComponent as FeedbackPhoneIcon } from "icons/phone_icon.svg";
import { useAppDispatch, useAppSelector } from "store";
import { getProfileByToken } from "utils/tokenProfile";
import { tokenSelector } from "store/auth/auth.selectors";
import { logoutAction } from "store/auth/auth.reducer";
import UserInfo from "components/UserInfo/UserInfo";
import SidebarMenu from "layouts/AuthLayout/SidebarMenu/SidebarMenu";
import styles from "./AuthLayout.module.scss";
import ContactModal from "./SidebarMenu/ContactModal/ContactModal";
import { ReactComponent as LogoutIcon } from "icons/logout_icon.svg";

const { SUPPORT_PHONE } = process.env;

const AuthLayout: FC<PropsWithChildren> = ({ children }) => {
  const dispatch = useAppDispatch();
  const token = useAppSelector(tokenSelector);
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

 
  const openContactModal = () => setIsContactModalOpen(true);
  const closeContactModal = () => setIsContactModalOpen(false);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const profile = useMemo<IUser | null>(() => getProfileByToken(token), [token]);


  if (!token) {
    return <Navigate to={ROUTES.LOGIN} replace state={{ from: location.pathname }} />;
  }

  const handleLogout = () => {
    dispatch(logoutAction());
  };

  const handleSidebarToggle = () => {
    setSidebarOpen((prev) => !prev);
  };

  const handleOverlayClick = () => {
    setSidebarOpen(false);
  };

  return (
    <div className={styles.layout}>
      
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div
            className={styles.sidebarTrigger}
            onClick={handleSidebarToggle}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSidebarToggle(); }}
            aria-label={sidebarOpen ? "Закрыть меню" : "Открыть меню"}
          >
            {sidebarOpen ? <CloseIcon /> : <HamburgerIcon />}
          </div>
          <LogoIcon className={styles.logo} />
        </div>
        
        <div className={styles.headerRight}>
          <Link to={`tel:${SUPPORT_PHONE}`} className={styles.phone}>
            <PhoneIcon className={styles.phoneIcon} />
            <span className={styles.phoneText}>8 800 600 92 26</span>
          </Link>
          {profile && (
            <UserInfo
              id={profile.idInvestor}
              name={
                profile.type !== USER_TYPE.UL
                  ? [profile.firstName, profile.lastName].filter(Boolean).join(" ") || profile.companyName || ""
                  : profile.companyName || ""
              }
              className={styles.userInfo}
            />
          )}
          <div className={styles.logoutMenu} onClick={handleLogout} role="button" tabIndex={0} aria-label="Выйти из системы">
            <LogoutIcon />
          </div>
        </div>
      </div>
      <div className={styles.body}>
        <div 
          className={cns(styles.overlay, { [styles.active]: sidebarOpen })} 
          onClick={handleOverlayClick}
        />
        
        <div className={cns(styles.sidebar, { [styles.sidebarOpen]: sidebarOpen })}>
          <SidebarMenu />
          <div className={styles.logout} onClick={handleLogout} role="button" tabIndex={0} aria-label="Выйти из системы">
            <ExitIcon />
            <span className={styles.logoutText}>Выход</span>
          </div>
          
        </div>
        <div className={styles.content}>{children}</div>
        <div className={styles.FeedbackPhoneIcon} onClick={openContactModal} role="button" tabIndex={0} aria-label="Обратная связь">
          <FeedbackPhoneIcon />
        </div>
        {isContactModalOpen && <ContactModal onClose={closeContactModal} />}
      
      </div>
      
    </div>
  );
};

export default AuthLayout;
